import { useState } from 'react';
import { D, st } from '../constants';

// Show enough decimals to be honest about small unit prices without printing
// noise on large ones. $0.2558/ft must not render as $0.26.
const money = p => {
  const n = Number(p) || 0;
  if (n === 0) return '$0.00';
  if (n < 0.1) return '$' + n.toFixed(4);
  if (n < 10) return '$' + n.toFixed(3).replace(/0$/, '');
  return '$' + n.toFixed(2);
};

export default function SupplyHouseCompare({ matPriceHistory }) {
  const [q, setQ] = useState('');
  const [multiOnly, setMultiOnly] = useState(true);
  const entries = Object.entries(matPriceHistory || {});

  if (entries.length === 0) {
    return (
      <div style={{ color: D.muted, fontSize: 13, marginTop: 20 }}>
        No price history yet. Import a supply house invoice or scan a receipt to start tracking prices.
      </div>
    );
  }

  const allSuppliers = [...new Set(entries.flatMap(([, h]) => (h || []).map(e => e.supplier)))]
    .filter(Boolean).sort();

  const rows = entries.map(([desc, hist]) => {
    const h = Array.isArray(hist) ? hist : [];
    const bySupplier = {};
    allSuppliers.forEach(s => {
      // Most recent entry for this supplier. Entries are stored newest-first,
      // but sort explicitly rather than trust it, and tolerate a missing date.
      const e = h.filter(x => x.supplier === s)
                 .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))[0];
      if (e) bySupplier[s] = e;
    });
    const present = Object.values(bySupplier);
    // Only like-for-like units are comparable. Treat a missing unit as 'ea',
    // which is what every pre-import entry effectively was.
    const units = [...new Set(present.map(e => e.unit || 'ea'))];
    const mixed = units.length > 1;
    const prices = present.map(e => Number(e.price)).filter(p => p > 0);
    const best = (!mixed && prices.length > 1) ? Math.min(...prices) : null;
    return { desc, bySupplier, best, mixed, units, n: present.length };
  }).filter(r => r.n > 0)
    .filter(r => !multiOnly || r.n > 1)
    .filter(r => !q.trim() || r.desc.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => a.desc.localeCompare(b.desc));

  const comparable = entries.filter(([, h]) =>
    new Set((h || []).map(e => e.supplier).filter(Boolean)).size > 1).length;

  return (
    <div>
      <div style={{ fontSize: 13, color: D.muted, marginBottom: 10 }}>
        Each supplier's most recent price per material. Green = lowest.
        {' '}<b style={{ color: D.text }}>{comparable}</b> of {entries.length} materials have prices from more than one supplier.
      </div>
      {comparable === 0 && multiOnly && (
        <div style={{ fontSize: 12, color: D.warning, background: D.warning + '18', border: '1px solid ' + D.warning + '55', borderRadius: 7, padding: '8px 10px', marginBottom: 12 }}>
          Nothing to compare yet — every price so far comes from one supplier.
          Scan or import an invoice from a second supply house and the same materials will line up side by side here.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...st.input, maxWidth: 260 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search materials" />
        <label style={{ fontSize: 12, color: D.muted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={multiOnly} onChange={e => setMultiOnly(e.target.checked)} />
          Only materials with 2+ suppliers
        </label>
        <span style={{ fontSize: 12, color: D.muted, marginLeft: 'auto' }}>{rows.length} shown</span>
      </div>
      <div style={{ overflowX: 'auto', border: `1px solid ${D.border}`, borderRadius: 8 }}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={{ ...st.th, textAlign: 'left', minWidth: 180 }}>Material</th>
              <th style={{ ...st.th, minWidth: 54 }}>Unit</th>
              {allSuppliers.map(s => <th key={s} style={{ ...st.th, minWidth: 120 }}>{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ desc, bySupplier, best, mixed, units }) => (
              <tr key={desc}>
                <td style={{ ...st.td, fontWeight: 500, fontSize: 13 }}>
                  {desc}
                  {mixed && <div style={{ fontSize: 10, color: D.warning, marginTop: 2 }}>
                    Not comparable — suppliers quote this per {units.join(' vs per ')}.
                  </div>}
                </td>
                <td style={{ ...st.td, textAlign: 'center', fontSize: 12, color: D.muted }}>
                  {mixed ? '—' : units[0]}
                </td>
                {allSuppliers.map(s => {
                  const e = bySupplier[s];
                  const isBest = e && best !== null && Number(e.price) === best;
                  return (
                    <td key={s} style={{
                      ...st.td, textAlign: 'center',
                      background: isBest ? D.success + '22' : 'transparent',
                      color: isBest ? D.success : D.text,
                      fontWeight: isBest ? 600 : 400,
                    }}>
                      {e ? (
                        <div>
                          <div>{money(e.price)}{mixed && <span style={{ fontSize: 10, color: D.muted }}>/{e.unit || 'ea'}</span>}</div>
                          {/* A per-foot price is hard to compare against a shelf price.
                              Showing what the actual coil costs makes the two commensurable
                              by eye even when the stored units differ. */}
                          {e.pkgQty > 0 && (
                            <div style={{ fontSize: 10, color: D.muted }}>
                              {e.pkgLabel}: {money(Number(e.price) * e.pkgQty)}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: isBest ? D.success : D.muted }}>{e.date || ''}</div>
                        </div>
                      ) : <span style={{ color: D.muted }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
